import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    TextField,
    Typography,
    Stack,
    List,
    ListItemButton,
    ListItemText,
    Chip,
    Box
} from '@mui/material';

export default function TransferMemberModal({ onClose, member, properties, onTransfer }) {
    const [filterText, setFilterText] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);

    const assignedPropertyIds = useMemo(
        () => new Set(member.property_ids ?? []),
        [member.property_ids]
    );

    const filteredProperties = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        return properties.filter(p => p.address.toLowerCase().includes(q));
    }, [properties, filterText]);

    const handleTransfer = () => {
        if (selectedProperty) {
            onTransfer(selectedProperty);
            onClose();
        }
    };

    return (
        <>
            <DialogTitle>
                <Typography sx={{ textTransform: 'capitalize' }}>
                    {`${member.first_name} ${member.last_name}`}
                </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body1">Transfer To</Typography>
                    <TextField
                        placeholder="Select Address"
                        fullWidth
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                    />
                    <List sx={{ width: 500, height: 350, overflow: 'auto' }}>
                        {filteredProperties.map(property => {
                            const isAlreadyAssigned = assignedPropertyIds.has(
                                property.id
                            );
                            const isInactive = property.status === 'inactive';
                            const isNonMember = property.type === 'non-member';

                            const isDisabled = isAlreadyAssigned || isNonMember;

                            let label = null;

                            if (isAlreadyAssigned) {
                                label = 'Already assigned';
                            } else if (isInactive) {
                                label = 'Inactive';
                            } else if (isNonMember) {
                                label = 'Non-member';
                            }

                            return (
                                <ListItemButton
                                    key={property.id}
                                    selected={selectedProperty?.id === property.id}
                                    disabled={isDisabled}
                                    onClick={() => {
                                        if (!isDisabled) {
                                            setSelectedProperty(property);
                                        }
                                    }}
                                >
                                    <ListItemText primary={property.address} />

                                    {label && (
                                        <Box sx={{ ml: 2 }}>
                                            <Chip
                                                label={label}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    )}
                                </ListItemButton>
                            );
                        })}
                        {filteredProperties.length === 0 && (
                            <ListItemText sx={{ p: 2 }} primary="No properties found." />
                        )}
                    </List>
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={handleTransfer}
                    color="primary"
                    variant="contained"
                    disabled={!selectedProperty}
                >
                    Transfer
                </Button>
            </DialogActions>
        </>
    );
}

TransferMemberModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    member: PropTypes.shape({
        first_name: PropTypes.string.isRequired,
        last_name: PropTypes.string.isRequired,
        property_ids: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    properties: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            address: PropTypes.string.isRequired,
            status: PropTypes.string,
            type: PropTypes.string
        })
    ).isRequired,
    onTransfer: PropTypes.func.isRequired
};
